// Raids (RAID; M14.2). PURE (ENG-1): the wave state machine only. The host
// (client/boot.ts) owns the world/entities — it spawns the raiders this module
// tells it to, reports how many are still alive, and applies the Hero-of-the-
// Village reward on victory. Wave *composition* is data (data/raids/*.json,
// BLK-4); this module only sequences it.
import type { Difficulty } from '../entities/entity';
import type { RaidTable, RaidWave } from '../registry/schemas';

/** Canonical spawn order within a wave — heavies first, so a ravager leads and
 *  the archers trail. Any raider present in a wave but missing here sorts last
 *  (alphabetically) so the expansion is always deterministic. */
const RAIDER_ORDER = ['ravager', 'evoker', 'vindicator', 'pillager', 'witch', 'vex'] as const;

/** The waves for a difficulty (peaceful never raids → no waves). */
export function wavesFor(table: RaidTable, difficulty: Difficulty): readonly RaidWave[] {
  if (difficulty === 'peaceful') return [];
  return table.waves[difficulty];
}

/** Total raiders in one wave. */
export function totalRaiders(wave: RaidWave): number {
  let n = 0;
  for (const c of Object.values(wave)) n += c;
  return n;
}

/** Expand a wave's `{mob: count}` map into a flat, deterministically ordered list
 *  of mob names (RAIDER_ORDER, then any extras alphabetically). */
export function waveMobs(wave: RaidWave): string[] {
  const names = Object.keys(wave).sort((a, b) => {
    const ia = RAIDER_ORDER.indexOf(a as (typeof RAIDER_ORDER)[number]);
    const ib = RAIDER_ORDER.indexOf(b as (typeof RAIDER_ORDER)[number]);
    if (ia !== ib) return (ia < 0 ? RAIDER_ORDER.length : ia) - (ib < 0 ? RAIDER_ORDER.length : ib) || a.localeCompare(b);
    return a.localeCompare(b);
  });
  const out: string[] = [];
  for (const name of names) for (let i = 0; i < wave[name]!; i++) out.push(name);
  return out;
}

export type RaidStatus = 'active' | 'victory' | 'defeat';
/** A phase transition the host reacts to (spawn SFX / bossbar / reward). */
export type RaidEvent = 'wave-start' | 'wave-clear' | 'victory' | 'defeat';

export interface RaidVec { x: number; y: number; z: number }

/**
 * One in-progress raid. The host drives it once per game tick via `tick(alive,
 * spawn)`: it begins each wave (calling `spawn` for every raider), and once the
 * host reports zero raiders alive it clears the wave and advances — reaching
 * `victory` after the final wave. `defeat()` ends it early (village lost /
 * abandoned). Bad-Omen *level* is carried through so the host can scale the
 * Hero-of-the-Village reward (RAID).
 */
export class Raid {
  status: RaidStatus = 'active';
  /** 0-based index of the wave being fought; −1 before the first has started. */
  waveIndex = -1;
  /** Ticks the raid has been running (bookkeeping / bossbar). */
  age = 0;
  private waveSpawned = false;
  /** Raiders spawned for the current wave (bossbar denominator). */
  waveTotal = 0;

  constructor(
    readonly center: RaidVec,
    readonly waves: readonly RaidWave[],
    readonly badOmen = 1,
  ) {
    if (waves.length === 0) this.status = 'victory'; // nothing to raid (peaceful)
  }

  get totalWaves(): number { return this.waves.length; }
  get active(): boolean { return this.status === 'active'; }
  /** 1-based wave number for display (0 before the first wave). */
  get waveNumber(): number { return this.waveIndex + 1; }

  /** Mob names for the wave currently being fought (empty before wave 1). */
  currentWaveMobs(): string[] {
    const w = this.waves[this.waveIndex];
    return w ? waveMobs(w) : [];
  }

  /**
   * Advance one tick. `alive` = this raid's raiders still alive (host counts
   * them from the entity list). `spawn(mob, i, n)` places raider `i` of `n` when
   * a wave begins. Returns the phase transition this tick, or null.
   */
  tick(alive: number, spawn: (mob: string, index: number, count: number) => void): RaidEvent | null {
    if (this.status !== 'active') return null;
    this.age++;
    if (!this.waveSpawned) {
      this.waveIndex++;
      if (this.waveIndex >= this.totalWaves) { this.status = 'victory'; return 'victory'; }
      const mobs = this.currentWaveMobs();
      this.waveTotal = mobs.length;
      mobs.forEach((m, i) => spawn(m, i, mobs.length));
      this.waveSpawned = true;
      return 'wave-start';
    }
    if (alive <= 0) {
      this.waveSpawned = false;
      // Final wave cleared → victory now; otherwise the next wave starts next tick.
      if (this.waveIndex + 1 >= this.totalWaves) { this.status = 'victory'; return 'victory'; }
      return 'wave-clear';
    }
    return null;
  }

  /** End the raid in defeat (all villagers dead, or the player abandoned it). */
  defeat(): RaidEvent | null {
    if (this.status !== 'active') return null;
    this.status = 'defeat';
    return 'defeat';
  }

  /** Serialize the raid's runtime progress (MB.6 persistence). The wave list is
   *  data (re-fetched from the raid table on load), so only progress is stored. */
  toJSON(): SavedRaid {
    return {
      center: { ...this.center }, badOmen: this.badOmen, status: this.status,
      waveIndex: this.waveIndex, age: this.age, waveSpawned: this.waveSpawned, waveTotal: this.waveTotal,
    };
  }

  /** Rebuild a raid mid-flight from a save + the difficulty's wave list. */
  static fromJSON(s: SavedRaid, waves: readonly RaidWave[]): Raid {
    const r = new Raid(s.center, waves, s.badOmen);
    r.status = s.status; r.waveIndex = s.waveIndex; r.age = s.age;
    r.waveSpawned = s.waveSpawned; r.waveTotal = s.waveTotal;
    return r;
  }
}

/** Persisted raid progress (MB.6). The wave list is not stored — it's data. */
export interface SavedRaid {
  center: RaidVec;
  badOmen: number;
  status: RaidStatus;
  waveIndex: number;
  age: number;
  waveSpawned: boolean;
  waveTotal: number;
}
